/**
 * Image Lightbox Component
 * Full-screen image gallery viewer with zoom and navigation
 */
import React, { useState, useCallback } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import { getFullImageUrl, getThumbnailUrl } from '@/lib/utils';

export const ImageGallery = ({ images, initialIndex = 0, onClose, isOpen }) => {
  // Build slides with full resolution URLs
  const slides = images.map((src) => ({
    src: getFullImageUrl(src),
    alt: 'Imagine produs',
  }));

  return (
    <Lightbox
      open={isOpen}
      close={onClose}
      index={initialIndex}
      slides={slides}
      plugins={[Zoom, Fullscreen, Thumbnails, Counter]}
      zoom={{
        maxZoomPixelRatio: 5,
        zoomInMultiplier: 2,
        doubleTapDelay: 300,
        doubleClickDelay: 300,
        doubleClickMaxStops: 2,
        keyboardMoveDistance: 50,
        wheelZoomDistanceFactor: 100,
        pinchZoomDistanceFactor: 150,
        scrollToZoom: true,
      }}
      thumbnails={{
        position: 'bottom',
        width: 60,
        height: 45,
        border: 2,
        borderRadius: 6,
        padding: 2,
        gap: 4,
        vignette: false,
      }}
      counter={{
        container: { style: { top: 0, bottom: 'unset' } },
      }}
      styles={{
        container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
        root: { 
          '--yarl__color_button': 'hsl(217, 91%, 60%)', 
          '--yarl__color_button_active': 'hsl(217, 91%, 53%)' 
        },
      }}
      controller={{
        closeOnBackdropClick: true,
        closeOnPullDown: true,
        closeOnPullUp: false,
      }}
      carousel={{
        finite: images.length <= 1,
        preload: 2,
        padding: '16px',
        spacing: '16px',
        imageFit: 'contain',
      }}
      animation={{
        fade: 250,
        swipe: 500,
        zoom: 300,
        easing: {
          fade: 'ease-out',
          swipe: 'ease-out',
          navigation: 'ease-in-out',
        },
      }}
    />
  );
};

/**
 * Clickable Image Grid that opens lightbox
 */
export const ImageGridWithLightbox = ({ images, className = '' }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  if (!images || images.length === 0) {
    return (
      <div className={`aspect-square rounded-2xl bg-gray-100 flex items-center justify-center ${className}`}>
        <span className="text-muted-foreground">Fără imagine</span>
      </div>
    );
  }

  // Single image
  if (images.length === 1) {
    return (
      <>
        <div 
          className={`aspect-square rounded-2xl overflow-hidden cursor-zoom-in ${className}`}
          onClick={() => openLightbox(0)}
        >
          <img
            src={images[0]}
            alt="Imagine produs"
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
        <ImageGallery
          images={images}
          initialIndex={lightboxIndex}
          isOpen={lightboxOpen}
          onClose={closeLightbox}
        />
      </>
    );
  }

  // Multiple images - grid layout
  return (
    <>
      <div className={`grid gap-2 ${className}`}>
        {/* Main image */}
        <div 
          className="aspect-square rounded-2xl overflow-hidden cursor-zoom-in"
          onClick={() => openLightbox(0)}
        >
          <img
            src={images[0]}
            alt="Imagine principală"
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
        
        {/* Thumbnail grid */}
        {images.length > 1 && (
          <div className="grid grid-cols-4 gap-2">
            {images.slice(1, 5).map((img, idx) => (
              <div
                key={idx}
                className="aspect-square rounded-xl overflow-hidden cursor-zoom-in relative"
                onClick={() => openLightbox(idx + 1)}
              >
                <img
                  src={img}
                  alt={`Imagine ${idx + 2}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
                {/* Show +X overlay on last thumbnail if more images */}
                {idx === 3 && images.length > 5 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">
                      +{images.length - 5}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageGallery
        images={images}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
      />
    </>
  );
};

export default ImageGallery;
